import './team-modal.scss';

import { Box, Stack } from '@mui/material';
import {
  ContactType,
  UserGroup,
  useGetBDMRegion,
  useGetRegion,
  useGetUsers,
  useUpsertBrokerageMutation,
} from '@converge/graphql';
import { CreateTeamsField, getCreateTeamFieldProps } from '@converge/data';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { NewFormTextField, NewTypeAhead } from '../../presentation/components';
import { useCallback, useEffect, useMemo } from 'react';
import { useConvergeNavigation, useConvergeSnackBar } from '../../contexts';

import { ApolloError } from '@apollo/client';
import { CreateTeamsFieldsSchema } from './create.teams.validation.schema';
import { DialogLayout } from '../../presentation/components/dialog-layout';
import { IPerson } from './accordion.form-container';
import { ISelectedTeams } from './teams-list';
import { NewConvergeButton } from '../../presentation/components/new-button';
import { getUserOptions } from '../submission';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

interface AddTeamsModalProps {
  onClose: () => void;
  title: string;
  selectedTeams?: ISelectedTeams | undefined | null;

  brokers?: IPerson[];
}

interface CreateTeamsFields {
  [CreateTeamsField.TeamName]: string | undefined;
  [CreateTeamsField.UwRegion]: string | undefined;
  [CreateTeamsField.PrimaryContact]: string | undefined;
  [CreateTeamsField.PrimaryContactEmail]: string | Date;
  [CreateTeamsField.BusinessDevelopmentManager]: string | undefined;
  [CreateTeamsField.BDMRegion]: string | undefined;
  [CreateTeamsField.Underwriter]: string | undefined;
}

export const AddTeamModal = ({
  onClose,
  title,
  selectedTeams,

  brokers,
}: AddTeamsModalProps) => {
  const formMethods = useForm<CreateTeamsFields>({
    resolver: yupResolver(CreateTeamsFieldsSchema),
    defaultValues: {},
  });
  const { data: region, loading: isUwRegionLoading } = useGetRegion();
  const { data: bdmRegion, loading: isBdmRegionLoading } = useGetBDMRegion();
  const { brokerageUuid } = useParams();
  const navigate = useConvergeNavigation();
  const { notify } = useConvergeSnackBar();
  const { loading, upsertEntity } = useUpsertBrokerageMutation();
  const { data: underwriterList } = useGetUsers({
    userGroups: [UserGroup.Underwriter],
  });
  const { data: businessDevManager } = useGetUsers({
    userGroups: [UserGroup.Bdm],
  });

  const UNDERWRITER_OPTIONS = useMemo(
    () => getUserOptions(underwriterList) ?? [],
    [underwriterList]
  );

  const BUSINESS_DEV_MANAGER_OPTIONS = useMemo(
    () => getUserOptions(businessDevManager) ?? [],
    [businessDevManager]
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const brokerList = brokers?.map((broker) => ({
    label: `${broker.node.firstName} ${broker.node.lastName}`,
    value: broker.node.uuid,
    data: broker.node.contacts?.edges
      .filter((item) => item.node.type === ContactType.Email)
      .map((email) => ({
        label: email.node.value,
        value: email.node.uuid,
      })),
  }));

  const UNDERWRITING_REGION_OPTIONS = region?.uwTerritories?.edges
    ?.map((parentOption) => {
      return {
        value: parentOption?.node?.uuid || '',
        label: parentOption?.node?.name || '',
      };
    })
    ?.sort((a, b) => a?.label.localeCompare(b?.label));

  const BDM_REGION_OPTIONS = bdmRegion?.bdmTerritories?.edges
    ?.map((bdmRegionOption) => {
      return {
        value: bdmRegionOption?.node?.uuid || '',
        label: bdmRegionOption?.node?.name || '',
      };
    })
    ?.sort((a, b) => a?.label.localeCompare(b?.label));

  const SubmitTeamHandler = useCallback(
    (): SubmitHandler<CreateTeamsFields> => async (fields) => {
      let TeamsResponse, TeamsResponseErrors;
      const teamsData = {
        entityUuid: brokerageUuid,
        teams: [
          {
            name: fields.teamName,
            bdmTerritoryUuids: fields?.bdmTerritoryUuid
              ? [fields?.bdmTerritoryUuid]
              : [],
            bdmUserUuid: fields?.bdmUserUuid ? fields?.bdmUserUuid : 'null',
            underwriterUserUuid: fields?.underwriterUserUuid
              ? fields?.underwriterUserUuid
              : 'null',
            uwTerritoryUuids: fields.uwRegion ? [fields.uwRegion] : [],
            leadPersonUuid: fields?.primaryContact
              ? fields?.primaryContact
              : 'null',
          },
        ],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;

      const selectedTeamsUuid = selectedTeams?.id;
      if (selectedTeamsUuid) {
        teamsData.teams[0].teamUuid = selectedTeamsUuid;
      }

      try {
        const { data, errors } = await upsertEntity({
          variables: { entityInput: teamsData },
        });
        TeamsResponse = data;
        TeamsResponseErrors = errors;

        notify(
          `Brokerage team has been ${selectedTeams ? 'updated' : 'added'} `,
          {
            variant: 'success',
          }
        );
        onClose();
        navigate.brokerage({ brokerageUuid: brokerageUuid });
      } catch (cause) {
        console.error(
          `Failed to ${
            selectedTeams ? 'update' : 'save'
          } brokerage team. Please try again. (${cause})`
        );
        if (cause instanceof ApolloError) {
          TeamsResponse = cause.graphQLErrors;
        }
      }
      const errorMessage = TeamsResponseErrors?.[0]?.message;
      if (errorMessage || !TeamsResponse) {
        notify(
          `Failed to ${
            selectedTeams ? 'update' : 'save'
          } team. Please try again.(${
            errorMessage || 'No data returned by the API'
          })`,
          {
            variant: 'error',
          }
        );
        return;
      }
    },
    [brokerageUuid, navigate, notify, upsertEntity, onClose, selectedTeams]
  );
  useEffect(() => {
    if (selectedTeams) {
      formMethods.setValue(
        CreateTeamsField.TeamName,
        selectedTeams?.teamName ?? ''
      );
      formMethods.setValue(
        CreateTeamsField.BDMRegion,
        selectedTeams?.bdmRegionUuid ?? ''
      );
      formMethods.setValue(
        CreateTeamsField.UwRegion,
        selectedTeams?.uwRegionUuid ?? ''
      );

      formMethods.setValue(
        CreateTeamsField.PrimaryContact,
        selectedTeams?.primaryContactUuid ?? ''
      );
      formMethods.setValue(
        CreateTeamsField.PrimaryContactEmail,

        selectedTeams?.primaryContactEmail ?? ''
      );

      formMethods.setValue(
        CreateTeamsField.BusinessDevelopmentManager,

        selectedTeams?.businessDevelopmentManagerUuid ?? ''
      );

      formMethods.setValue(
        CreateTeamsField.Underwriter,
        selectedTeams?.underwriterUuid ?? ''
      );
    } else {
      formMethods.reset();
    }
  }, [formMethods, selectedTeams]);

  return (
    <DialogLayout
      sx={{
        overflow: 'hidden',
        '& .MuiPaper-root.MuiDialog-paper': {
          minWidth: '1200px',
          minHeight: '510px',
          overflow: 'hidden !important',
          fontFamily: 'Red Hat Text, Arial, sans-serif',
        },
        '& .MuiDialogContent-root ': {
          overflow: 'hidden !important',
        },
      }}
      open
      onClose={onClose}
      showCloseButton={true}
      onCloseIconClick={onClose}
      className="add-team"
      header={<Box className="modal-title-team"> {title}</Box>}
    >
      <FormProvider {...formMethods}>
        <Stack className="row-container">
          <Box className="fields-left-team">
            <NewFormTextField
              {...getCreateTeamFieldProps(CreateTeamsField.TeamName)}
              required
              placeholder="Enter Team Name"
              dataTestId="team-name-input"
            />
          </Box>
          <Box className="fields-middle-team">
            <NewTypeAhead
              {...getCreateTeamFieldProps(CreateTeamsField.PrimaryContact)}
              dataTestId="team-primary-contact-dropdown"
              placeholder="Select Contact"
              options={brokerList}
              onChangeOption={(item) => {
                if (Array.isArray(item?.data) && item.data.length > 0) {
                  formMethods.setValue(
                    CreateTeamsField.PrimaryContactEmail,
                    item.data[0]?.label ?? ''
                  );
                } else {
                  formMethods.setValue(
                    CreateTeamsField.PrimaryContactEmail,
                    ''
                  );
                }
              }}
              maxHeightDropdown={300}
            />
          </Box>
          <Box className="fields-right-team">
            <NewFormTextField
              placeholder="name@domain.com"
              disabled={true}
              {...getCreateTeamFieldProps(CreateTeamsField.PrimaryContactEmail)}
              dataTestId="team-primary-contact-email-input"
            />
          </Box>
        </Stack>
        <Stack className="row-container">
          <Box className="fields-second-row-left">
            <NewTypeAhead
              {...getCreateTeamFieldProps(
                CreateTeamsField.BusinessDevelopmentManager
              )}
              dataTestId="team-bdm-dropdown"
              placeholder="Select BDM"
              options={BUSINESS_DEV_MANAGER_OPTIONS || []}
              maxHeightDropdown={200}
            />
          </Box>
          <Box className="fields-second-row-first-middle">
            <NewTypeAhead
              {...getCreateTeamFieldProps(CreateTeamsField.BDMRegion)}
              options={BDM_REGION_OPTIONS}
              placeholder="Select BDM Region"
              dataTestId="team-bdm-region-dropdown"
              isLoading={isBdmRegionLoading}
            />
          </Box>
          <Box className="fields-second-row-middle">
            <NewTypeAhead
              {...getCreateTeamFieldProps(CreateTeamsField.Underwriter)}
              options={UNDERWRITER_OPTIONS || []}
              dataTestId="team-underwriter-dropdown"
              placeholder="Select UW"
              maxHeightDropdown={200}
            />
          </Box>
          <Box className="fields-second-row-right  newType">
            <NewTypeAhead
              {...getCreateTeamFieldProps(CreateTeamsField.UwRegion)}
              options={UNDERWRITING_REGION_OPTIONS}
              placeholder="Select Region"
              dataTestId="team-uw-region-dropdown"
              isLoading={isUwRegionLoading}
            />
          </Box>
        </Stack>
        <Stack className="button-container-team">
          <Box className="close-btn-Container">
            <NewConvergeButton
              label="CANCEL"
              variant="outlined"
              onClick={onClose}
              data-testid="cancel-team-button"
            />
          </Box>
          <Box>
            <NewConvergeButton
              loading={loading}
              onClick={formMethods.handleSubmit(SubmitTeamHandler())}
              data-testid="submit-team-button"
              label={`${selectedTeams ? 'Update' : 'Add'} Team`}
            />
          </Box>
        </Stack>
      </FormProvider>
    </DialogLayout>
  );
};
